import { observer } from "mobx-react";
import {
  IReactiveCourseWorkContentItem,
  IReactiveEventContentItem,
  IReactiveFileContentItem,
  IReactiveFolderContentItem,
  IReactiveMediaContentItem,
  IReactiveNewsContentItem,
  isCoursework,
} from "../SchoolFeedContentItem";
import React, { MutableRefObject, useRef } from "react";
import { useStores } from "../../../stores/useStores";
import { useTranslateClassName } from "../../../hooks/useTranslateClassName";
import classNames from "classnames";
import styles from "./styles/ListItemGroup.module.scss";
import {
  capitalize,
  getPlatform,
  getSocialIcon,
} from "../../../utils/StringUtilities";
import { sourceTypesEnum } from "../../../utils/SourceUtils";
import ClickableLink from "../../utilities/ClickableLink";
import FontAwesome from "../../utilities/FontAwesome";
import { ContentItemDate } from "./ContentItemDate";
import Markdown from "../../utilities/Markdown";
import moment from "moment/moment";
import { ItemDetails } from "./ItemDetails";
import { JoinDiscussionButton } from "./JoinDiscussionButton";
import { scrollItemId } from "./ListItemGroup";
import { isFile, isNews, isEvent } from "./utils";
import { useFeatureFlags } from "../../../utils/featureFlags/useFeatureFlags";
import { TranslateButton } from "./TranslateButton";

export const Item = observer(
  (props: {
    itemObj:
      | IReactiveEventContentItem
      | IReactiveNewsContentItem
      | IReactiveCourseWorkContentItem
      | IReactiveFolderContentItem
      | IReactiveFileContentItem
      | IReactiveMediaContentItem;
    allowCuration: boolean;
    deleteItem: (
      item: IReactiveEventContentItem | IReactiveNewsContentItem,
    ) => void;
    unpublishItem: (
      item:
        | IReactiveEventContentItem
        | IReactiveNewsContentItem
        | IReactiveCourseWorkContentItem
        | IReactiveFolderContentItem
        | IReactiveFileContentItem
        | IReactiveMediaContentItem,
    ) => void;
    handleClick: () => void;
    handleKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  }) => {
    const { userStore, sidebarStore, organizationStore } = useStores();
    const itemRef: MutableRefObject<any> = useRef(null);
    const translateClassName = useTranslateClassName();
    const { show_translate_button } = useFeatureFlags();

    const itemClassName = classNames({
      [styles.item]: true,
      [styles.itemHidden]: !props.itemObj.published,
    });

    let sourceIconLink;
    const platform = getPlatform(props.itemObj.type);
    const isParentSquare = platform === sourceTypesEnum.PARENTSQUARE;
    if (
      props.itemObj.third_party_url &&
      ![
        sourceTypesEnum.ICAL,
        sourceTypesEnum.RSS,
        sourceTypesEnum.PARENTSQUARE,
      ].includes(platform)
    ) {
      const iconClassName = classNames({
        [styles[`sourceIcon${capitalize(platform)}`]]: true,
        [styles.sourceIcon]: true,
      });
      sourceIconLink = (
        <ClickableLink
          className={iconClassName}
          href={props.itemObj.third_party_url}
          title={props.itemObj.source.account_title}
        >
          <FontAwesome name={getSocialIcon(platform, true)} />
        </ClickableLink>
      );
    }
    const descriptionClassname = classNames({
      [styles.description]: true,
      [styles.descriptionWithPrompt]: !!props.itemObj.discussion_prompt,
    });

    return (
      <div
        className={`${itemClassName} ${scrollItemId}`}
        id={`${props.itemObj.id}`}
        ref={itemRef}
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          props.handleClick();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            props.handleClick();
          }
          props.handleKeyDown && props.handleKeyDown(e);
        }}
      >
        {userStore.isEditor && (
          <div className={styles.curationContainer}>
            <button
              aria-label={"Edit Content Item"}
              onClick={(e) => {
                e.stopPropagation();
                sidebarStore.setSidebar({
                  view: "SchoolFeedPost",
                  contentItem: props.itemObj,
                  handleDelete: (contentItem) => {
                    props.deleteItem(contentItem);
                  },
                });
              }}
            >
              <FontAwesome ariaHidden prefix={"fas"} name={"fa-pencil-alt"} />
            </button>
          </div>
        )}

        <div className={styles.header}>
          {props.itemObj.title && (
            <div className={styles.title}>
              {isNews(props.itemObj) && (
                <div className={styles.dateContainer}>
                  <ContentItemDate contentItem={props.itemObj} />
                  <span className={styles.orgTitle}>
                    {" · " + props.itemObj.organization.title}
                  </span>
                </div>
              )}
              <Markdown
                withStyles={true}
                className={translateClassName}
                source={props.itemObj.title}
              />
              {isNews(props.itemObj) && show_translate_button && (
                <TranslateButton itemObj={props.itemObj} />
              )}
            </div>
          )}

          {isCoursework(props.itemObj) &&
            props.itemObj.json_data?.coursework?.dueDate && (
              <div className={styles.date} style={{ fontWeight: "bold" }}>
                Due Date:{" "}
                {moment(props.itemObj.json_data?.coursework?.dueDate).format(
                  "MMM D, YYYY",
                )}
              </div>
            )}
          {isEvent(props.itemObj) && (
            <ItemDetails
              itemObj={props.itemObj}
              style={styles.dateLocationContainer}
            />
          )}
          {props.itemObj.description && (
            <div className={descriptionClassname}>
              {sourceIconLink}
              <Markdown
                withStyles={true}
                className={translateClassName}
                source={props.itemObj.description}
              />
            </div>
          )}
          {isParentSquare && props.itemObj.third_party_url && (
            <ClickableLink
              className={styles.parentsquareLink}
              href={props.itemObj.third_party_url}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              View on ParentSquare →
            </ClickableLink>
          )}
          {props.itemObj.discussion_prompt && (
            <div className={styles.discussionPrompt}>
              <span>Prompt</span>
              <Markdown
                className={translateClassName}
                source={props.itemObj.discussion_prompt}
              />
            </div>
          )}
          {props.itemObj.meritchat_enabled &&
            organizationStore.schoolFeedEnabledForOrganization &&
            isNews(props.itemObj) && (
              <div className={styles.joinTheDiscussionContainer}>
                <JoinDiscussionButton itemObj={props.itemObj} />
              </div>
            )}
          {isFile(props.itemObj) && (
            <ItemDetails
              itemObj={props.itemObj}
              style={styles.dateLocationContainer}
            />
          )}
        </div>
      </div>
    );
  },
);
